import { Link } from "react-router-dom";
import { useTranslation } from "@/i18n";
import RankaroLogo from "@/components/shared/RankaroLogo";
import { Mail } from "lucide-react";

type FooterLink = {
  labelKey: string;
  href: string;
};

const Footer = () => {
  const { t } = useTranslation();

  const links = {
    producto: [
      { labelKey: "footer.links.features", href: "/#features" },
      { labelKey: "footer.links.pricing", href: "/#pricing" },
      { labelKey: "footer.links.plugin", href: "/plugin-seo-woocommerce" },
    ] as FooterLink[],
    seo: [
      { labelKey: "footer.links.seoWoocommerce", href: "/seo-woocommerce" },
      { labelKey: "footer.links.descripciones", href: "/descripciones-productos-seo" },
      { labelKey: "footer.links.alternativaYoast", href: "/alternativa-yoast" },
      { labelKey: "footer.links.pluginSeo", href: "/plugin-seo-woocommerce" },
      { labelKey: "footer.links.rankMathVsRankaro", href: "/rank-math-vs-rankaro" },
    ] as FooterLink[],
    recursos: [
      { labelKey: "footer.links.seoGuide", href: "/seo-woocommerce" },
    ] as FooterLink[],
    empresaLegal: [
      { labelKey: "footer.links.about", href: "/about" },
      { labelKey: "footer.links.contact", href: "/contact" },
      { labelKey: "footer.links.affiliates", href: "/affiliates" },
      { labelKey: "footer.links.privacy", href: "/privacy" },
      { labelKey: "footer.links.terms", href: "/terms" },
      { labelKey: "footer.links.cookies", href: "/cookies" },
    ] as FooterLink[],
  };

  const renderLink = (link: FooterLink, i: number) => (
    <li key={i}>
      <Link
        to={link.href}
        className="text-sm text-secondary-foreground/70 hover:text-secondary-foreground transition-colors link-animated"
      >
        {t(link.labelKey)}
      </Link>
    </li>
  );

  return (
    <footer className="py-16 bg-secondary text-secondary-foreground relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb w-[400px] h-[400px] -bottom-40 left-1/4 opacity-10" style={{ background: 'hsl(var(--primary) / 0.2)' }} />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center mb-4 group" aria-label="Rankaro - Inicio">
              <RankaroLogo variant="full" size={36} className="transition-transform group-hover:scale-105" />
            </Link>
            <p className="text-sm text-secondary-foreground/70 mb-4">
              {t('footer.tagline')}
              <br />
              {t('footer.taglineLine2')}
            </p>
            <div className="flex gap-4">
              <a 
                href="https://twitter.com/rankaroio" 
                className="w-10 h-10 rounded-lg bg-secondary-foreground/10 flex items-center justify-center text-secondary-foreground/60 hover:bg-primary hover:text-white transition-all"
                aria-label="Twitter"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a 
                href="https://linkedin.com/company/rankaro" 
                className="w-10 h-10 rounded-lg bg-secondary-foreground/10 flex items-center justify-center text-secondary-foreground/60 hover:bg-primary hover:text-white transition-all"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>

          {/* Producto */}
          <div>
            <h3 className="font-semibold mb-4 text-base">{t('footer.product')}</h3>
            <ul className="space-y-3">
              {links.producto.map(renderLink)}
            </ul>
          </div>

          {/* SEO Guides */}
          <div>
            <h3 className="font-semibold mb-4 text-base">{t('footer.seoGuides')}</h3>
            <ul className="space-y-3">
              {links.seo.map(renderLink)}
            </ul>
          </div>

          {/* Recursos */}
          <div>
            <h3 className="font-semibold mb-4 text-base">{t('footer.resources')}</h3>
            <ul className="space-y-3">
              {links.recursos.map(renderLink)}
            </ul>
          </div>

          {/* Empresa + Legal */}
          <div>
            <h3 className="font-semibold mb-4 text-base">{t('footer.legal')}</h3>
            <ul className="space-y-3">
              {links.empresaLegal.map(renderLink)}
            </ul>

            {/* Contact emails */}
            <h3 className="font-semibold mb-3 mt-6 text-base">{t('footer.contactTitle')}</h3>
            <ul className="space-y-2">
              <li>
                <a href="mailto:support@rankaro.io" className="text-sm text-secondary-foreground/70 hover:text-secondary-foreground transition-colors flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  support@rankaro.io
                </a>
                <span className="text-xs text-secondary-foreground/50 ml-5">{t('footer.supportLabel')}</span>
              </li>
              <li>
                <a href="mailto:bill@rankaro.io" className="text-sm text-secondary-foreground/70 hover:text-secondary-foreground transition-colors flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  bill@rankaro.io
                </a>
                <span className="text-xs text-secondary-foreground/50 ml-5">{t('footer.billingLabel')}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-secondary-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-secondary-foreground/70">
            {t('footer.copyright')}
          </p>
          <div className="flex items-center gap-2 text-sm text-secondary-foreground/70">
            <span>🇨🇱</span>
            <span>{t('footer.madeFor')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
